import { FiServer, FiDatabase, FiShield, FiZap, FiGlobe, FiTrendingUp, FiCpu, FiCode, FiLock } from "react-icons/fi";
import { FaNodeJs, FaPhp, FaPython } from "react-icons/fa";
import { SiDotnet, SiLaravel, SiSymfony, SiCodeigniter } from "react-icons/si";
import { DiDjango } from "react-icons/di";
import { LuSettings, LuUsers, LuShieldCheck, LuPercent, LuShield, LuCircleCheck, LuCpu, LuClipboardList, LuCode, LuBug, LuRocket, LuDatabase, LuClock } from "react-icons/lu";

export const backendBenefits = [
  {
    icon: <FiLock className="w-8 h-8" />,
    title: "Military-Grade Security",
    desc: "Robust encryption and secure API design ensuring your business data and user privacy are always protected.",
  },
  {
    icon: <FiDatabase className="w-8 h-8" />,
    title: "Scalable Architecture",
    desc: "We build backend systems designed to handle millions of requests, scaling horizontally as your company grows.",
  },
  {
    icon: <FiZap className="w-8 h-8" />,
    title: "Optimized Performance",
    desc: "Fine-tuned database queries and efficient server-side logic for the lowest possible response times.",
  },
  {
    icon: <FiGlobe className="w-8 h-8" />,
    title: "Cloud Native",
    desc: "Seamless integration with AWS, Azure, and Google Cloud for resilient and high-availability systems.",
  },
];

export const backendServices = [
  {
    icon: <FaNodeJs size={36} />,
    title: "NodeJS Development",
    description: "Build scalable and real-time backend applications using event-driven, non-blocking architecture.",
    image: "/images/WebAppDevelopment/Node_JS_Development.png",
    link: "/nodejs-development-services-india",
    points: ["Event-driven non-blocking I/O", "ExpressJS & NestJS expertise", "Real-time WebSockets integration"],
  },
  {
    icon: <SiDotnet size={36} />,
    title: ".NET Development",
    description: "Develop secure and enterprise-grade backend systems with high performance and stability.",
    image: "/images/WebAppDevelopment/NET_Development.png",
    link: "/dotnet-development-services-india",
    points: ["Enterprise security architecture", "High-performance C# code", "Robust WebAPI development"],
  },
  {
    icon: <SiSymfony size={36} />,
    title: "Symfony Development",
    description: "Create modular and maintainable PHP applications for complex backend requirements.",
    image: "/images/WebAppDevelopment/Symfony_development.png",
    link: "/symfony-development-services-india",
    points: ["Modular PHP architectures", "Strict compliance & clean code", "Highly maintainable codebase"],
  },
  {
    icon: <SiLaravel size={36} />,
    title: "Laravel Development",
    description: "Build elegant and scalable backend solutions with powerful ORM and caching support.",
    image: "/images/WebAppDevelopment/Laravel_Development.png",
    link: "/laravel-development-services-india",
    points: ["Eloquent ORM & database tuning", "Robust caching & queue workers", "Elegant Eloquent & Blade usage"],
  },
  {
    icon: <SiCodeigniter size={36} />,
    title: "Codeigniter Development",
    description: "Develop lightweight and fast PHP applications with simple configuration and quick deployment.",
    image: "/images/WebAppDevelopment/codeigner_development.png",
    link: "/codeigniter-development-services-india",
    points: ["Small footprint, high performance", "Zero-configuration setup", "Fast & lightweight applications"],
  },
  {
    icon: <DiDjango size={36} />,
    title: "Django Development",
    description: "Create secure and scalable Python backend systems with rapid development capabilities.",
    image: "/images/WebAppDevelopment/django_development.png",
    link: "/django-development-services-india",
    points: ["Rapid prototyping features", "Built-in secure user models", "Highly scalable django REST framework"],
  },
];

export const backendProcessSteps = [
  {
    step: "01",
    title: "Data Architecture Design",
    description: "Mapping out data structures and entity relationships to ensure a logical and efficient information flow.",
    points: ["Database schema design", "Relationship mapping", "Scalability planning"],
  },
  {
    step: "02",
    title: "API Strategy & Definition",
    description: "Designing RESTful or GraphQL endpoints that are intuitive, secure, and well-documented.",
    points: ["API endpoint mapping", "Authentication strategy", "Documentation setup"],
  },
  {
    step: "03",
    title: "Server-Side Coding",
    description: "Implementing core business logic with clean, tested code in your preferred technology stack.",
    points: ["Clean code practices", "Module development", "Unit testing"],
  },
  {
    step: "04",
    title: "Security & Validation",
    description: "Hardening the system against vulnerabilities with robust sanitization and authorization layers.",
    points: ["Input validation", "Threat modeling", "OWASP compliance"],
  },
  {
    step: "05",
    title: "Cloud Infrastructure",
    description: "Deploying to high-availability servers with CI/CD pipelines for seamless updates and maintenance.",
    points: ["CI/CD setup", "Load balancer configuration", "Autoscaling policies"],
  },
];

export const backendFaqs = [
  {
    q: "Which database should I use?",
    a: "It depends on your data type. We use RDBMS (PostgreSQL/MySQL) for structured data and NoSQL (MongoDB) for flexible, high-speed needs.",
  },
  {
    q: "How do you handle high traffic spikes?",
    a: "We architect backends using microservices and load balancing, ensuring the system automatically scales server instances during peak loads.",
  },
];

export const backendTechStack = ["Node.js", ".NET", "Python", "PHP", "Laravel", "PostgreSQL", "MongoDB", "Redis", "Docker"];

export const backendHeroData = {
  backgroundImage: "/images/serviceBanners/backEndTechnology.webp",
  whiteImage: "/images/serviceBanners/BackendBannerW.webp",
  subTitle: "Backend Development Services",
  title: (
    <>
      Robust, Scalable & Secure <br /> <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-600">Backend Systems</span>
    </>
  ),
  description: "Build high-performance backend services with secure APIs, optimized databases, and scalable server-side architecture for reliable application performance.",
  primaryButton: {
    title: "Get a Free Quote",
    onClick: () => console.log("Get a Free Quote clicked"),
  },
  secondaryButton: {
    title: "View Our Work",
    onClick: () => console.log("View Our Work clicked"),
  },
  featureData: [
    {
      icon: "/images/services/website/high_performance.webp",
      title: "High Performance",
      desc: "Fast and optimized server response and query handling.",
      iconColor: "#a855f7",
    },
    {
      icon: "/images/services/website/responsive.webp",
      title: "Autoscaling Setup",
      desc: "Automatically scale backend resources based on traffic load.",
      iconColor: "#22d3ee",
    },
    {
      icon: "/images/services/website/secure.webp",
      title: "Secure Encryption",
      desc: "Strong data protection with industry-standard security practices.",
      iconColor: "#a855f7",
    },
    {
      icon: "/images/services/website/clean_code.webp",
      title: "Clean API Design",
      desc: "Well-structured REST and GraphQL APIs for easy integration.",
      iconColor: "#34d399",
    },
  ],
  floatingIcons: [
    {
      image: "/images/services/website/nodejs.webp",
      top: "16%",
      right: "8%",
      containerSize: 80,
      iconSize: 60,
      glowColor: "rgba(59,130,246,0.35)",
      iconGlow: "rgba(59,130,246,0.9)",
    },
    {
      image: "/images/services/website/python.svg",
      top: "8%",
      right: "16%",
      containerSize: 70,
      iconSize: 50,
      glowColor: "rgba(59,130,246,0.35)",
      iconGlow: "rgba(59,130,246,0.9)",
    },
    {
      image: "/images/services/website/reactjs.svg",
      top: "20%",
      right: "22%",
      containerSize: 60,
      iconSize: 40,
      glowColor: "rgba(59,130,246,0.35)",
      iconGlow: "rgba(59,130,246,0.9)",
    },
  ],
};
export const backendStatsData = [
  {
    icon: LuSettings,
    value: "200+",
    label: "APIs Integrated",
    colorClass: "text-[#00d2ff]",
    bgClass: "bg-[#00d2ff]/10",
    borderClass: "border-[#00d2ff]/20",
    glowClass: "shadow-[0_0_20px_rgba(0,210,255,0.15)]",
  },
  {
    icon: LuUsers,
    value: "80+",
    label: "Happy Clients",
    colorClass: "text-[#a855f7]",
    bgClass: "bg-[#a855f7]/10",
    borderClass: "border-[#a855f7]/20",
    glowClass: "shadow-[0_0_20px_rgba(168,85,247,0.15)]",
  },
  {
    icon: LuShieldCheck,
    value: "99.99%",
    label: "Server Uptime",
    colorClass: "text-[#06b6d4]",
    bgClass: "bg-[#06b6d4]/10",
    borderClass: "border-[#06b6d4]/20",
    glowClass: "shadow-[0_0_20px_rgba(6,182,212,0.15)]",
  },
  {
    icon: LuCpu,
    value: "15M+",
    label: "API Requests/Day",
    colorClass: "text-[#3b82f6]",
    bgClass: "bg-[#3b82f6]/10",
    borderClass: "border-[#3b82f6]/20",
    glowClass: "shadow-[0_0_20px_rgba(59,130,246,0.15)]",
  },
  {
    icon: LuPercent,
    value: "45%",
    label: "Latency Reduction",
    colorClass: "text-[#d946ef]",
    bgClass: "bg-[#d946ef]/10",
    borderClass: "border-[#d946ef]/20",
    glowClass: "shadow-[0_0_20px_rgba(217,70,239,0.15)]",
  },
  {
    icon: LuShield,
    value: "100%",
    label: "Data Protection",
    colorClass: "text-[#6366f1]",
    bgClass: "bg-[#6366f1]/10",
    borderClass: "border-[#6366f1]/20",
    glowClass: "shadow-[0_0_20px_rgba(99,102,241,0.15)]",
  },
];

export const backendSolutionsAndProcessData = {
  buildSubtitle: "WHAT WE BUILD",
  buildTitle: "Scalable Backend Solutions for Modern Applications",
  buildCards: [
    {
      title: "REST & GraphQL APIs",
      desc: "Build secure and high-performance APIs for scalable backend communication.",
      icon: LuCode,
      colorClass: "text-[#00d2ff]",
      bgClass: "bg-[#00d2ff]/10",
      borderClass: "border-[#00d2ff]/20 hover:border-[#00d2ff]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(0,210,255,0.25)]",
    },
    {
      title: "Database Architectures",
      desc: "Design fast and reliable SQL and NoSQL database systems for data-heavy applications.",
      icon: LuDatabase,
      colorClass: "text-[#3b82f6]",
      bgClass: "bg-[#3b82f6]/10",
      borderClass: "border-[#3b82f6]/20 hover:border-[#3b82f6]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(59,130,246,0.25)]",
    },
    {
      title: "Microservices Architecture",
      desc: "Create modular backend systems for better scalability and independent deployment.",
      icon: LuSettings,
      colorClass: "text-[#a855f7]",
      bgClass: "bg-[#a855f7]/10",
      borderClass: "border-[#a855f7]/20 hover:border-[#a855f7]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(168,85,247,0.25)]",
    },
    {
      title: "Real-time Backends",
      desc: "Enable live data updates using WebSockets and event-driven architecture.",
      icon: LuClock,
      colorClass: "text-[#06b6d4]",
      bgClass: "bg-[#06b6d4]/10",
      borderClass: "border-[#06b6d4]/20 hover:border-[#06b6d4]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(6,182,212,0.25)]",
    },
    {
      title: "Cloud Infrastructure",
      desc: "Deploy scalable systems on AWS, Azure, and GCP with CI/CD automation.",
      icon: LuRocket,
      colorClass: "text-[#6366f1]",
      bgClass: "bg-[#6366f1]/10",
      borderClass: "border-[#6366f1]/20 hover:border-[#6366f1]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(99,102,241,0.25)]",
    },
    {
      title: "Custom Backend Solutions",
      desc: "Develop tailored backend logic and integrations for specific business needs.",
      icon: LuCpu,
      colorClass: "text-[#d946ef]",
      bgClass: "bg-[#d946ef]/10",
      borderClass: "border-[#d946ef]/20 hover:border-[#d946ef]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(217,70,239,0.25)]",
    },
  ],
  processSubtitle: "BACKEND ENGINEERING PROCESS",
  processTitle: "Backend Engineering Process",
  processSteps: [
    {
      step: "01",
      title: "Data Design",
      description: "Define scalable database schemas, entities, and data relationships.",
      icon: LuClipboardList,
    },
    {
      step: "02",
      title: "API Definition",
      description: "Design secure and structured API endpoints with proper access control.",
      icon: LuCode,
    },
    {
      step: "03",
      title: "Core Development",
      description: "Build clean and modular backend logic using modern programming stacks.",
      icon: LuSettings,
    },
    {
      step: "04",
      title: "Security Validation",
      description: "Ensure authentication, sanitization, and API security standards.",
      icon: LuShieldCheck,
    },
    {
      step: "05",
      title: "Performance Optimization",
      description: "Optimize database queries and backend execution speed.",
      icon: LuCircleCheck, // Updated to ensure build compliance
    },
    {
      step: "06",
      title: "CI/CD Deployment",
      description: "Automate deployment pipelines for reliable and continuous delivery.",
      icon: LuRocket,
    },
  ],
  benefitsSubtitle: "WHY CHOOSE TYCHO BACKEND SOLUTIONS?",
  benefitsTitle: "Why Our Backend Solutions Are the Right Choice",
  benefits: [
    "Strong backend security",
    "Scalable database systems",
    "Low API response time",
    "Cloud-native deployment",
    "Automated testing pipelines",
    "OpenAPI documentation support",
    "Clean modular codebase",
  ],
  logoImage: "/images/serviceBanners/backEndTechnology.webp",
  whiteLogoImage: "/images/serviceBanners/BackendBannerW.webp",
};

export const backendPortfolioData = {
  subtitle: "OUR WORK",
  title: "Secure Backends We're Proud Of",
  buttonText: "View All Projects",
  buttonLink: "#all-projects",
  apps: [
    {
      title: "Enterprise eCommerce Core",
      badge: "Microservices Architecture",
      badgeColor: "bg-[#00d2ff]/10 text-[#00d2ff] border-[#00d2ff]/20",
      desc: "Designed and engineered a highly scalable eCommerce backend capable of processing 10,000+ orders per minute.",
      downloads: "50M+ Req",
      rating: "4.9",
      image: "/images/WebAppDevelopment/backend_Developement.png",
    },
    {
      title: "PaySecure Gateway",
      badge: "FinTech Server",
      badgeColor: "bg-[#a855f7]/10 text-[#a855f7] border-[#a855f7]/20",
      desc: "Robust API gateway facilitating multi-channel payments with military-grade encryption and PCI-DSS compliance.",
      downloads: "20M+ Txns",
      rating: "5.0",
      image: "/images/WebAppDevelopment/backend_Developement.png",
    },
    {
      title: "StreamSync Engine",
      badge: "WebSockets Server",
      badgeColor: "bg-[#3b82f6]/10 text-[#3b82f6] border-[#3b82f6]/20",
      desc: "Low-latency streaming server powering live chat, comments, and media pipelines with high reliability.",
      downloads: "1M+ Connc",
      rating: "4.8",
      image: "/images/WebAppDevelopment/backend_Developement.png",
    },
  ],
};

export const backendTechnologiesData = {
  techTitle: "TECHNOLOGIES WE USE",
  technologies: [
    { name: "REST API", icon: "RestAPI" },
    { name: "SQLite", icon: "SQLite" },
    { name: "Firebase", icon: "Firebase" },
    { name: "Provider", icon: "Provider" },
  ],
  trustTitle: "TRUSTED BY BUSINESSES WORLDWIDE",
  trustClients: [
    { image: "/images/logo/google_logo.png", alt: "google_logo" },
    { image: "/images/logo/microsoft_logo.png", alt: "microsoft_logo" },
    { image: "/images/logo/amazon_logo.png", alt: "amazon_logo" },
    { image: "/images/logo/Adobe_Corporate_logo.png", alt: "adobe_logo" },
    { image: "/images/logo/IBM_logo.png", alt: "ibm_logo" },
    { image: "/images/logo/Oracle_logo.png", alt: "oracle_logo" },
  ],
};

export const backendAboutData = [
  {
    title: "Backend App Development Services for Scalable Digital Systems",
    content: (
      <>
        Modern applications depend on reliable backend systems that handle data processing, integrations, and performance without disruption. Effective{" "}
        <b className="text-text-primary font-semibold">backend app development services</b> help businesses build stable infrastructures that support scalability, improve system
        efficiency, and strengthen digital operations. Many organizations struggle with slow performance, integration issues, and limited scalability that affect user experiences.
        At <b className="text-text-primary font-semibold">Tycho Technologies</b>, backend systems are developed around business requirements, long-term flexibility, and operational
        performance to help companies build dependable digital ecosystems.
      </>
    ),
  },
  {
    title: "Backend Development Company for High-Performance Applications",
    content: (
      <>
        A strong backend system is essential for keeping applications secure, responsive, and operational under increasing demand. A trusted{" "}
        <b className="text-text-primary font-semibold">backend development company</b> helps businesses create structured server environments that improve application reliability
        and support future growth. Companies investing in <b className="text-text-primary font-semibold">custom backend development services</b> often improve operational
        efficiency because systems are designed around specific workflows and business objectives. Tycho Technologies focuses on practical backend architectures that improve
        performance while supporting long-term digital scalability and business continuity.
      </>
    ),
  },
  {
    title: "Server Side Development Services for Smarter Infrastructure",
    content: (
      <>
        Businesses handling large amounts of user activity and real-time operations require systems that remain stable under pressure. Reliable{" "}
        <b className="text-text-primary font-semibold">server side development services</b> help organizations improve system communication, application functionality, and overall
        operational consistency. Companies looking to strengthen modern backend ecosystems often combine infrastructure planning with{" "}
        <a href="https://tychotechnologies.com/nodejs-development-services-india" className="text-cyan-400 underline hover:text-cyan-300">
          node js development services
        </a>{" "}
        to improve responsiveness and support scalable application performance.
      </>
    ),
  },
  {
    title: "Why Businesses Choose a Backend Development Company in India",
    content: (
      <>
        The demand for a trusted <b className="text-text-primary font-semibold">backend development company in india</b> continues to grow as businesses seek secure and scalable
        digital infrastructures. Companies investing in <b className="text-text-primary font-semibold">backend app development services india</b> often prioritize performance,
        reliability, and long-term maintainability for complex systems. Businesses partnering with Tycho Technologies gain access to tailored backend frameworks designed around
        measurable business objectives and technical requirements. Many organizations also strengthen enterprise system performance through{" "}
        <a href="https://tychotechnologies.com/dotnet-development-services-india" className="text-cyan-400 underline hover:text-cyan-300">
          dot net development services
        </a>{" "}
        to support scalable and future-ready applications.
      </>
    ),
  },
];
